import React from 'react';
import { useFormikContext } from 'formik';
import { get } from 'lodash';

import StateMultiselectFormik from '~/components/core/Formik/StatesMultiselectFormik/StateMultiSelectFormik';
import CountryAutocompleteFormik from '~/components/CountryAutocompleteFormik';
import type { AdvancedSearchFilterTypes } from '~/components/GenericTemplates/types';
import { ADVANCED_TEMPLATES_SEARCH_KEYS } from '~/components/GenericTemplates/utils/genericTemplatesUtils';
import useOrganization from '~/components/OrganizationContext';
import { useSysconfig } from '~/components/SystemConfiguration/SystemConfigurationScreen';
import TextFieldFormik from '~/components/TextFieldFormik';
import { CoveragesMultiselectFormik } from '~/components/TPA/Coverages/CoveragesMultiselectFormik';
import LobMultiselectFormik from '~/components/TPA/LOB/LobMultiselectFormik';
import SubOrganizationMultiselectFormik from '~/components/TPA/SubOrganizations/SubOrganizationMultiselectFormik';
import { CONFIGURATION_FEATURES_NAMES } from '~/Types';
import { getOrganizationCountryCode, isCountryWithStates, isFeatureEnabled, isOrganizationWithStates } from '~/Utils';
import cn from '~/Utils/cn';

interface GenericTemplateAdvancedSearchFiltersFormikProps {
  // Screen-specific extra filter(s) rendered on the Template Name row (e.g. the MOI email screen's
  // methods multi-select). Each node occupies the next cell of the same grid row.
  additionalFilters?: React.ReactNode;
}

const GenericTemplateAdvancedSearchFiltersFormik: React.FC<GenericTemplateAdvancedSearchFiltersFormikProps> = ({
  additionalFilters,
}) => {
  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  // @ts-ignore
  const { supportedClaimTypes } = useOrganization();
  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  // @ts-ignore
  const { organization } = useSysconfig();
  const { values, isSubmitting } = useFormikContext<AdvancedSearchFilterTypes>();
  const multiCountryOrganization = isFeatureEnabled(
    organization,
    CONFIGURATION_FEATURES_NAMES.MULTI_COUNTRY_ORGANIZATION
  );

  const shouldDisplayStates = isOrganizationWithStates(organization) || multiCountryOrganization;

  const subOrganizationIds = organization.sub_organizations.map((sub_org: { id: number }) => sub_org.id);
  let columnsAmount = 2;
  if (multiCountryOrganization) {
    columnsAmount += 1;
  }
  if (shouldDisplayStates) {
    columnsAmount += 1;
  }
  if (organization.sub_organizations_enabled) {
    columnsAmount += 1;
  }
  const gridColumnsClassName: Record<number, string> = {
    2: 'grid-cols-2',
    3: 'grid-cols-3',
    4: 'grid-cols-4',
    5: 'grid-cols-5',
  };
  return (
    <>
      <div className={cn('mb-16 grid gap-32', gridColumnsClassName[columnsAmount])}>
        <div>
          <TextFieldFormik id="template_name" label="Template Name" fullWidth disabled={isSubmitting} />
        </div>
        {additionalFilters ? <div>{additionalFilters}</div> : null}
      </div>
      <div className={cn('mb-32 grid gap-32', gridColumnsClassName[columnsAmount])}>
        {subOrganizationIds.length > 0 ? (
          <div>
            <SubOrganizationMultiselectFormik
              subOrganizationsFieldId={ADVANCED_TEMPLATES_SEARCH_KEYS.SUB_ORGANIZATION_IDS}
              disabled={isSubmitting}
            />
          </div>
        ) : null}
        <div>
          <LobMultiselectFormik
            lobFieldId={ADVANCED_TEMPLATES_SEARCH_KEYS.LOBS}
            disabled={isSubmitting}
            subOrganizationIds={
              organization.sub_organizations_enabled
                ? get(values, ADVANCED_TEMPLATES_SEARCH_KEYS.SUB_ORGANIZATION_IDS) || subOrganizationIds
                : []
            }
          />
        </div>
        <div>
          <CoveragesMultiselectFormik
            coveragesFieldId={ADVANCED_TEMPLATES_SEARCH_KEYS.COVERAGE_KEYS}
            lobs={get(values, ADVANCED_TEMPLATES_SEARCH_KEYS.LOBS) || supportedClaimTypes || []}
            label="Select Coverages"
            subOrganizationIds={
              get(values, ADVANCED_TEMPLATES_SEARCH_KEYS.SUB_ORGANIZATION_IDS) || subOrganizationIds || []
            }
            disabled={isSubmitting}
          />
        </div>
        {multiCountryOrganization ? (
          <CountryAutocompleteFormik
            id={ADVANCED_TEMPLATES_SEARCH_KEYS.COUNTRY}
            countriesConfigurationKey="contact_location"
            disabled={isSubmitting}
            statesFieldIdToResetOnChange={ADVANCED_TEMPLATES_SEARCH_KEYS.STATES}
          />
        ) : null}
        {shouldDisplayStates ? (
          <div>
            <StateMultiselectFormik
              statesFieldId={ADVANCED_TEMPLATES_SEARCH_KEYS.STATES}
              disabled={isSubmitting || !(multiCountryOrganization ? isCountryWithStates(values.country) : true)}
              country={
                multiCountryOrganization && isCountryWithStates(values.country)
                  ? values.country
                  : getOrganizationCountryCode(organization)
              }
            />
          </div>
        ) : null}
      </div>
    </>
  );
};

export default GenericTemplateAdvancedSearchFiltersFormik;
